import React, { useState } from "react";

import { Grid, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { HiOutlinePlusCircle } from "react-icons/hi";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { isValidPhoneNumber } from "react-phone-number-input";
import { v4 as uuid } from "uuid";
import isEmail from "validator/lib/isEmail";

import { Input } from "../../../../../../../components/Form";
import AutoSuggest from "../../../../../../../components/Form/AutoSuggest";
import PhoneInput from "../../../../../../../components/Form/PhoneInput";
import { Customer } from "../../../../../../../models/customer";
import {
  Communication,
  CustomerDetails,
} from "../../../../../../../models/shipments/shipments.tracking";
import { CommunicationsFromOptions } from "../../../../../../../util/enums";
import getOptionLabelCustomer from "../../../../../../../util/getOptionLabelCustomer";
import { Options, getSelectOptions } from "../../../../../../../util/getStatusOptions";
import useStyles from "./styles";

enum OptionType {
  SMS = "sms",
  EMAIL = "email",
}

type CustomerAttributes = {
  name: string;
  lastName: string;
  email: string;
  phone: String;
};

interface CustomerDetailsProps {
  indexOf: number;
  error?: string;
  customerList?: Customer[];
  customerDetails?: CustomerDetails;
  customerCommunication?: Communication;
  getError?: (error: string) => string | undefined;
  getHasError?: (error: string) => boolean;
  updateCustomer?: (customer: CustomerDetails) => void;
  updateCommunication?: (communications?: Communication) => void;
  addNewCustomer?: () => void;
  deleteCustomer?: () => void;
}

const CustomerDetailsComponent = (props: CustomerDetailsProps) => {
  const {
    indexOf,
    customerDetails,
    customerList,
    customerCommunication,
    getError,
    getHasError,
    updateCustomer,
    updateCommunication,
    addNewCustomer,
    deleteCustomer,
  } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();
  const [customerFrom, setCustomerFrom] = useState<string>("");

  const { viaOptions } = getSelectOptions();

  const isCustomerSelected =
    !!customerDetails &&
    !!customerDetails?.customer &&
    !!customerDetails?.customer.id &&
    typeof customerDetails?.customer.id === "number";

  const getCustomerValue = (attribute: string) => {
    if (customerDetails && customerDetails?.customer) {
      return customerDetails.customer[attribute as keyof CustomerAttributes] ?? "";
    }

    return "";
  };

  const getAutosuggestValue = () => {
    const customerFrom = customerCommunication?.from;

    if (
      customerFrom === CommunicationsFromOptions.EMAIL ||
      customerFrom === CommunicationsFromOptions.SMS
    )
      return viaOptions.filter((option) => option.value === customerFrom);
    if (customerFrom === CommunicationsFromOptions.BOTH) return viaOptions;

    return [];
  };

  const handleSetCustomerCommunication = (from: string) => {
    if (from === "") {
      if (updateCommunication) updateCommunication();
    } else if (customerDetails?.customer?.id) {
      if (customerCommunication) {
        if (updateCommunication) updateCommunication({ ...customerCommunication, from });
      } else if (updateCommunication)
        updateCommunication({ customerId: customerDetails?.customer?.id, from });
    }
    setCustomerFrom(from);
  };

  const handleSetCustomerFields = (customer?: Customer) => {
    if (!customer) {
      if (updateCommunication) updateCommunication();

      if (updateCustomer)
        updateCustomer({
          ...customerDetails,
          customer: undefined,
        });

      return;
    }

    if (customerFrom !== "") {
      if (customerCommunication) {
        if (updateCommunication)
          updateCommunication({ ...customerCommunication, from: customerFrom });
      } else if (updateCommunication)
        updateCommunication({ customerId: customer.id, from: customerFrom });
    }

    if (updateCustomer)
      updateCustomer({
        ...customerDetails,
        customer: {
          ...customer,
          phone: !customer?.phone?.includes("+") ? `+${customer?.phone}` : customer?.phone,
        },
      });
  };

  const alignItems =
    getHasError && getHasError(`customers[${indexOf}].customer.lastName`) ? "center" : "flex-end";

  const getAutosuggestOptions = () => {
    const hasValidEmail =
      customerDetails?.customer?.email && isEmail(customerDetails?.customer?.email);
    const hasValidPhoneNumber =
      customerDetails?.customer?.phone && isValidPhoneNumber(customerDetails?.customer?.phone);

    if (hasValidEmail && hasValidPhoneNumber) return viaOptions;
    if (!hasValidEmail && hasValidPhoneNumber)
      return viaOptions.filter((option) => option.value === OptionType.SMS);

    if (hasValidEmail && !hasValidPhoneNumber)
      return viaOptions.filter((option) => option.value === OptionType.EMAIL);

    return [];
  };

  return (
    <Grid
      item
      container
      direction="row"
      alignItems="flex-start"
      spacing={2}
      className={classes.mb16}
    >
      <Grid item xs={12} sm={6} md={2} lg={2}>
        <Input
          label={`${translate("order")} #`}
          placeholder="123456789"
          name="orderNumber"
          data-cy="input-order-number"
          value={customerDetails?.order ?? ""}
          onChange={(event) => {
            if (updateCustomer) {
              updateCustomer({
                ...customerDetails,
                order: event.target.value,
              });
            }
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2} lg={2}>
        <AutoSuggest
          data-cy="autosugges-first-name-customer"
          noOptionsText={translate("searchOrAddACustomer")}
          label={translate("firstName")}
          name="firstName"
          inputValue={getCustomerValue("name")}
          getOptionLabel={(customer: Customer) => getOptionLabelCustomer(customer)}
          getOptionSelected={(customer: Customer, value: Customer) => customer.id === value.id}
          options={customerList}
          onChange={(customer: Customer) => handleSetCustomerFields(customer)}
          onChangeInput={(value) => {
            if (!isCustomerSelected) {
              if (updateCustomer) {
                updateCustomer({
                  ...customerDetails,
                  customer: {
                    ...customerDetails?.customer,
                    id: uuid(),
                    name: value,
                  },
                });
              }
            }
          }}
          error={!!getHasError && getHasError(`customers[${indexOf}].customer.name`)}
          errorMessage={getError && getError(`customers[${indexOf}].customer.name`)}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2} lg={2}>
        <Input
          name="lastName"
          label={translate("lastName")}
          placeholder={translate("lastName")}
          disabled={!!isCustomerSelected}
          value={customerDetails?.customer?.lastName}
          onChange={(event) => {
            if (updateCustomer) {
              updateCustomer({
                ...customerDetails,
                customer: {
                  ...customerDetails?.customer,
                  lastName: event.target.value,
                },
              });
            }
          }}
          error={!!getHasError && getHasError(`customers[${indexOf}].customer.lastName`)}
          errorMessage={getError && getError(`customers[${indexOf}].customer.lastName`)}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2} lg={2}>
        <Input
          name="email"
          label={translate("email")}
          placeholder="email@email.com"
          disabled={!!isCustomerSelected}
          value={customerDetails?.customer?.email}
          onChange={(event) => {
            if (updateCustomer) {
              updateCustomer({
                ...customerDetails,
                customer: {
                  ...customerDetails?.customer,
                  email: event.target.value,
                },
              });
            }
          }}
          error={!!getHasError && getHasError(`customers[${indexOf}].customer.email`)}
          errorMessage={getError && getError(`customers[${indexOf}].customer.email`)}
        />
      </Grid>
      <Grid item xs={10} sm={6} md={2} lg={2}>
        <PhoneInput
          name="phone"
          label={translate("phone")}
          placeholder="61430111900"
          defaultCountry="AU"
          international
          disabled={!!isCustomerSelected}
          value={customerDetails?.customer?.phone}
          onChange={(value) => {
            if (updateCustomer) {
              updateCustomer({
                ...customerDetails,
                customer: {
                  ...customerDetails?.customer,
                  phone: value,
                },
              });
            }
          }}
          error={!!getHasError && getHasError(`customers[${indexOf}].customer.phone`)}
          errorMessage={getError && getError(`customers[${indexOf}].customer.phone`)}
        />
      </Grid>
      <Grid item container xs={12} sm={6} md={2} lg={2} alignItems={alignItems}>
        <Grid item xs={10} sm={10} md={10} lg={10}>
          <AutoSuggest
            dataCy="autosuggest-from-with-badge"
            label={translate("contactVia")}
            placeholder={getAutosuggestValue()?.length === 0 ? translate("selectAnOption") : ""}
            getOptionLabel={(option: Options) => {
              return option.label;
            }}
            getOptionSelected={(option: Options, value: Options) => option?.value === value?.value}
            value={getAutosuggestValue()}
            multiple
            onChange={(options: Options[]) => {
              const valueOptions = options.map((option: Options) => option.value);

              if (
                valueOptions.includes(CommunicationsFromOptions.SMS) &&
                valueOptions.includes(CommunicationsFromOptions.EMAIL)
              )
                handleSetCustomerCommunication(CommunicationsFromOptions.BOTH);
              else if (
                valueOptions.includes(CommunicationsFromOptions.SMS) &&
                !valueOptions.includes(CommunicationsFromOptions.EMAIL)
              )
                handleSetCustomerCommunication(CommunicationsFromOptions.SMS);
              else if (
                !valueOptions.includes(CommunicationsFromOptions.SMS) &&
                valueOptions.includes(CommunicationsFromOptions.EMAIL)
              )
                handleSetCustomerCommunication(CommunicationsFromOptions.EMAIL);
              else if (options.length === 0) {
                handleSetCustomerCommunication("");
              }
            }}
            options={getAutosuggestOptions()}
            disabled={
              !Object.values(customerDetails?.customer ?? {}).length ||
              !customerDetails?.customer?.email?.trim() ||
              !customerDetails?.customer?.phone?.trim()
            }
          />
        </Grid>
        <Grid item xs={2} sm={2} md={2} lg={2}>
          {indexOf === 0 && (
            <IconButton onClick={() => addNewCustomer && addNewCustomer()}>
              <HiOutlinePlusCircle color="#308CDB" />
            </IconButton>
          )}
          {indexOf > 0 && (
            <IconButton onClick={() => deleteCustomer && deleteCustomer()}>
              <IoMdCloseCircleOutline color="#F97066" />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CustomerDetailsComponent;
